import { ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatStatusLabel } from "@sd/constants/ticketStatuses";
import { hasCreatedDateFilter } from "@sd/lib/ticketCreatedDateFilter";
import type { CreatedDateFilterState } from "@sd/lib/ticketCreatedDateFilter";
import { TICKET_TYPE_FILTER_OPTIONS } from "@sd/types/ticket";

import { resolveFilterOptionLabel } from "@sd/lib/ticketFilterApi";

type ProjectOption = { value: string; label: string };

function projectLabel(id: string, options: ProjectOption[]): string {
  return resolveFilterOptionLabel(id, options, "Selected project");
}

function typeFilterLabel(typeFilter: string): string | null {
  if (!typeFilter || typeFilter === "all") return null;
  return (
    TICKET_TYPE_FILTER_OPTIONS.find(option => option.value === typeFilter)
      ?.label ?? typeFilter
  );
}

export function TicketListDrillDownBanner({
  variant,
  onBack,
  backLabel,
  projectOptions,
  projectFilters,
  statusFilters,
  typeFilter = "all",
  createdDateFilter,
}: {
  variant: "tracker-report" | "status-dashboard";
  onBack: () => void;
  backLabel: string;
  projectOptions: ProjectOption[];
  projectFilters: string[];
  statusFilters: string[];
  typeFilter?: string;
  createdDateFilter?: CreatedDateFilterState;
}) {
  const projectNames = projectFilters.map(id =>
    projectLabel(id, projectOptions)
  );

  const breadcrumb =
    variant === "tracker-report"
      ? (["Reports", "Tracker", projectNames[0]] as const).filter(Boolean)
      : (
          [
            "Dashboard Project Status",
            projectNames.length > 0 ? projectNames.join(", ") : null,
          ] as const
        ).filter(Boolean);

  const chips: { key: string; label: string }[] = [];

  if (variant === "status-dashboard" && projectNames.length > 0) {
    chips.push({
      key: "project",
      label: `Project: ${projectNames.join(", ")}`,
    });
  }

  for (const status of statusFilters) {
    chips.push({
      key: `status-${status}`,
      label: formatStatusLabel(status),
    });
  }

  const typeLabel = typeFilterLabel(typeFilter);
  if (typeLabel) {
    chips.push({ key: "type", label: `Type: ${typeLabel}` });
  }

  if (createdDateFilter && hasCreatedDateFilter(createdDateFilter)) {
    const from = createdDateFilter.from?.trim();
    const to = createdDateFilter.to?.trim();
    const range =
      from && to ? `${from} – ${to}` : from ? `From ${from}` : to ? `To ${to}` : "";
    if (range) {
      chips.push({ key: "created", label: `Created: ${range}` });
    }
  }

  return (
    <div className="mb-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2.5 text-foreground"
          onClick={onBack}
          title={backLabel}
          aria-label={backLabel}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <nav
          className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-muted-foreground"
          aria-label="Breadcrumb"
        >
          {breadcrumb.map((part, index) => (
            <span key={`${part}-${index}`} className="inline-flex items-center gap-1">
              {index > 0 ? <ChevronRight className="h-3 w-3 shrink-0" /> : null}
              <span
                className={
                  index === breadcrumb.length - 1
                    ? "font-medium text-foreground"
                    : undefined
                }
              >
                {part}
              </span>
            </span>
          ))}
        </nav>
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chips.map(chip => (
            <Badge
              key={chip.key}
              variant="secondary"
              className="border-transparent bg-muted/60 text-[11px] font-normal text-foreground"
            >
              {chip.label}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
